/**
 * Outbound deliveries API client.
 *
 * Mirrors `backend/src/hub/routes/deliveries.py`. One row per
 * external side-effect (HTTP request, connector push, future kinds).
 * The drain worker advances the status machine; this client lets the
 * UI list / inspect / manually retry or cancel rows.
 */
import { api } from "./api";


// Mirror of the backend status machine in
// ``hub.outbound.drain`` — exactly 4 states. There is NO "failed"
// state at runtime: a retryable failure stays as ``pending`` with
// ``last_error`` populated and ``next_attempt_at`` advanced. The only
// terminal failure state is ``dead_letter`` (budget exhausted or
// non-retryable). See docs/topics/engines/outbound-idempotency.md.
export type DeliveryStatus =
  | "pending"
  | "in_flight"
  | "succeeded"
  | "dead_letter";


export interface DeliverySummary {
  id: string;
  sink_kind: string;
  target_key: string;
  status: DeliveryStatus;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  last_error: string | null;
  source_execution_id: string | null;
  source_node_id: string | null;
  payload_size_bytes: number | null;
  created_at: string;
  updated_at: string | null;
  completed_at: string | null;
}


/** ADR-0008 PR3 — one snapshotted SinkBranch entry on the parent's
 *  delivery. Read off ``dispatch_metadata.branches`` (snapshot taken at
 *  enqueue time, immune to later workflow edits). Used to render the
 *  manual-action affordances + the inline timeline. */
export interface DeliveryBranchSnapshot {
  id: string;
  label?: string | null;
  trigger: "auto" | "manual" | "both";
  linked_workflow_id: string | null;
}


/** One entry in ``OutboundDelivery.subflow_fires``. Append-only audit
 *  trail of every sub-flow trigger on the delivery (auto + manual),
 *  in chronological order. */
export interface SubflowFire {
  branch_id: string;
  triggered_by: "auto" | "manual";
  /** ISO timestamp. Backend key is ``triggered_at``. */
  triggered_at: string;
  /** Spawned sub-flow's WorkflowExecution.id, resolved server-side from
   *  the DBOS workflow id. Null when no execution row matches yet (or
   *  DBOS isn't running in dev/test). */
  sub_execution_id: string | null;
  /** Raw DBOS workflow id the dispatcher recorded (pre-resolution).
   *  Kept for debugging; the UI links via ``sub_execution_id``. */
  dbos_workflow_id?: string | null;
  /** ``user_id`` for manual fires, ``"system"`` for auto. */
  actor?: string;
  /** Audit log row id, for cross-referencing. */
  audit_id?: string;
}


export interface DeliveryDetail extends DeliverySummary {
  credential_id: string | null;
  payload_blob_ref: string;
  payload_content_type: string | null;
  headers: Record<string, unknown>;
  dispatch_metadata: Record<string, unknown>;
  last_response: Record<string, unknown> | null;
  payload_preview: string | null;
  idempotency_key: string;
  /** ADR-0008 PR3 — terminal-state metadata. ``null`` while the
   *  delivery is still in flight or before classification ran. */
  outcome_branch: string | null;
  /** Append-only timeline of sub-flow triggers. */
  subflow_fires: SubflowFire[];
}


export interface DeliveryActionResponse {
  delivery_id: string;
  branch_id: string;
  triggered_by: "manual";
  actor: string;
  sub_execution_id: string | null;
  audit_id: string;
}


export interface DeliveryListResponse {
  items: DeliverySummary[];
  total: number;
  limit: number;
  offset: number;
}


export interface DeliveryListParams {
  status?: DeliveryStatus;
  sink_kind?: string;
  target_key?: string;
  source_execution_id?: string;
  limit?: number;
  offset?: number;
}


export const deliveriesApi = {
  list: (params?: DeliveryListParams) =>
    api.get<DeliveryListResponse>("/deliveries", {
      // Cast: DeliveryListParams' fields are all valid QueryValue
      // shapes (string | number | undefined) — TS just lacks the
      // index signature on the typed shape.
      query: params as Record<string, string | number | undefined>,
    }),

  get: (id: string) => api.get<DeliveryDetail>(`/deliveries/${id}`),

  retry: (id: string) =>
    api.post<DeliveryDetail>(`/deliveries/${id}/retry`),

  cancel: (id: string) =>
    api.post<DeliveryDetail>(`/deliveries/${id}/cancel`),

  /** ADR-0008 PR3 — fire a sub-flow on a delivery branch manually.
   *  Backend enforces the branch is declared on the source sink AND
   *  has ``trigger ∈ {manual, both}`` AND a non-null linked workflow.
   *  Idempotent by design: re-firing appends a new ``subflow_fires``
   *  entry; the linked sub-flow MUST dedupe on ``delivery.id``. */
  executeAction: (deliveryId: string, branchId: string) =>
    api.post<DeliveryActionResponse>(
      `/deliveries/${deliveryId}/actions/${encodeURIComponent(branchId)}`,
    ),
};
